<template>
  <div>
    <data-table
      ref="dataTableRef"
      :config="dataTableConfig"
      :endpoint="endpoint"
      :filterConfig="filterConfig"
      :refreshButton="refreshButtonConfig"
      :csvButton="csvButtonConfig"
      @data-loaded="handleDataLoaded"
      @data-error="handleDataError">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useFiltro } from '../../composables/useFiltro';

interface DataTableInstance {
  loadData: () => void;
}

// Get i18n composable
const { t } = useI18n();
const { initialized } = useFiltro();

// Reactive data
const dataTableRef = ref<DataTableInstance | null>(null);

// Computed properties
const endpoint = computed(() => '/index.php/relatorios/vendas_operadores/lista_vendas');

const tableColumns = computed(() => {
  return [
    {
      data: 'franquia',
      title: t('Franquia'),
      width: 150,
      sortable: true,
      footerType: 'unique'
    },
    {
      data: 'operador',
      title: t('Operador'),
      width: 150,
      sortable: true
    },
    {
      data: 'data',
      title: t('Data'),
      width: 180,
      sortable: true,
      renderType: 'datetime'
    },
    {
      data: 'servico_nome',
      title: t('Serviço'),
      width: 200,
      sortable: true
    },
    {
      data: 'servicoscategoria_nome',
      title: t('Categoria'),
      width: 150,
      sortable: true
    },
    {
      data: 'cliente_nome',
      title: t('Cliente'),
      width: 200,
      sortable: true
    },
    {
      data: 'total_compromisso',
      title: t('Valor'),
      width: 120,
      sortable: true,
      renderType: 'currency',
      footerType: 'sum'
    }
  ];
});

const dataTableConfig = computed(() => {
  return {
    tableId: 'tabelaVendasOperadores',
    filename: `vendas_operadores_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    pageLength: 100,
    pagination: true,
    footerLabel: t('Totais'),
    rowSelection: 'single',
    resizableColumns: true,
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const filterConfig = computed(() => {
  return {
    searchName: t('Procurar'),
    choosePlaceholder: t('Escolha'),
    refreshName: t('Atualizar'),
    printName: t('Imprimir'),
    debug: false,
    columns: {
      'franquia': 'select',
      'operador': 'select',
      'data': 'text',
      'servico_nome': 'select',
      'servicoscategoria_nome': 'text',
      'cliente_nome': 'text',
      'total_compromisso': 'range'
    }
  };
});

const csvButtonConfig = computed(() => {
  return {
    show: true,
    text: t('CSV'),
    loading: false
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

// Methods
const loadData = () => {
  const stack = new Error().stack;
  log('VendasOperadoresTable', 'loadData', 'Triggering table refresh', {
    callStack: stack?.split('\n').slice(1, 4).join('\n') // Log first 3 stack frames
  });
  if (dataTableRef.value) {
    dataTableRef.value.loadData();
  }
};

const handleDataLoaded = (data: unknown[]): void => {
  log('VendasOperadoresTable', 'handleDataLoaded', 'Data loaded', data.length, 'rows');
  eventBus.emit('globalMessage.success', t(`Carregados`) + ` ${data.length} ` + t(`registros de vendas`));
};

const handleDataError = (error: { message?: string } | Error): void => {
  log('VendasOperadoresTable', 'handleDataError', 'Error loading data', error);
  const message = error instanceof Error ? error.message : error.message || '';
  eventBus.emit('globalMessage.error', t('Erro ao carregar vendas dos operadores: ') + message);
};

// Named handler for proper cleanup
const onFilterUpdated = (): void => {
  log('VendasOperadoresTable', 'event:filter.updated', 'Received, reloading data');
  loadData();
};

// Lifecycle
onMounted(() => {
  log('VendasOperadoresTable', 'mounted', 'Setting up event listeners', {
    filterInitialized: initialized.value
  });

  // Don't load data on mount - let DataTable handle initial load via autoLoad
  // Only reload when filter is explicitly updated
  log('VendasOperadoresTable', 'mounted', 'Initial data load will be handled by DataTable autoLoad');

  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  log('VendasOperadoresTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
});

// Expose methods for parent components
defineExpose({
  loadData
});
</script>

<style scoped>
</style>
